import { defaults } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  PDF_PAGE_DEFAULT_HEIGHT,
  PDF_PAGE_DEFAULT_WIDTH,
} from '../../iframe/additional-apps.utils';
import { FrameItem } from '../../../../models/session';
import { BoardItemConfig, CreationContext } from './board-item-configurations';

export interface ImagesSizes {
  range: string;
  size: { width: number; height: number };
}

export class PDFImagesConfigurations extends BoardItemConfig {
  name: string;
  imagesSizes?: ImagesSizes[];
  contentId: string;
  numberOfPages?: number;
  imagesToken?: string;
  imagesPrefix?: string;
  selectedIndices?: number[];
  // Durable Firebase Storage URL of the original uploaded PDF/office file
  // (in `pdf-uploads/`) this board item was converted from. Persisted as
  // `resource_state.resource_url` so the board is traceable back to its source.
  sourceUrl?: string;
  // Indicating that the stacking order have been manipulated after the item was migrated from HTML to fabric
  migratedFromHtmlToFabric: boolean;

  static defaultBoardItemConfigurations: Partial<BoardItemConfig> = {
    width: PDF_PAGE_DEFAULT_WIDTH,
    height: PDF_PAGE_DEFAULT_HEIGHT,
    locked: true,
  };

  constructor(
    configurations: Partial<PDFImagesConfigurations> & Partial<BoardItemConfig>,
    globalCreationParams: CreationContext,
  ) {
    super(
      {
        ...PDFImagesConfigurations.defaultBoardItemConfigurations,
        ...configurations,
      },
      globalCreationParams,
    );

    const defaultPDFConfigurations = { name: 'untitled.pdf', contentId: uuidv4() };

    const pdfConfigurations = defaults(configurations, defaultPDFConfigurations);

    this.imagesSizes = pdfConfigurations.imagesSizes;
    this.name = pdfConfigurations.name;
    this.contentId = pdfConfigurations.contentId;
    this.numberOfPages = pdfConfigurations.numberOfPages;
    this.imagesToken = pdfConfigurations.imagesToken;
    this.imagesPrefix = pdfConfigurations.imagesPrefix;
    this.selectedIndices = pdfConfigurations.selectedIndices;
    this.sourceUrl = pdfConfigurations.sourceUrl;
    this.migratedFromHtmlToFabric = true;
  }

  static fromYCanvasItemObject(
    frameItem: FrameItem,
    globalCreationsParams: CreationContext,
  ): PDFImagesConfigurations {
    const configurations = BoardItemConfig.fromYCanvasItemObject(
      frameItem,
      globalCreationsParams,
      PDFImagesConfigurations.defaultBoardItemConfigurations,
    );

    const numberOfPages = frameItem.resource_state?.numberOfPages ?? 0;

    let selectedIndices = frameItem.resource_state?.selectedIndices ?? [];
    if (selectedIndices?.length === 0) {
      // When the array is empty, we mean by that the all the pages are needed,
      // and we store it as an empty array to save memory as this is the most common case.
      // We populate the empty array with all the indexes of the PDF so that the rest of the app handles it normally
      selectedIndices = Array.from({ length: numberOfPages ?? 0 }, (_, i) => i) as number[];
    }

    return new PDFImagesConfigurations(
      {
        ...configurations,
        imagesSizes: frameItem.resource_state?.imagesSizes,
        name: frameItem.resource_state?.resource_name,
        numberOfPages,
        imagesToken: frameItem.resource_state?.imagesToken,
        imagesPrefix: frameItem.resource_state?.imagesPrefix,
        selectedIndices,
        sourceUrl: frameItem.resource_state?.resource_url,
        contentId: frameItem.content_id,
        migratedFromHtmlToFabric: frameItem.migratedFromHtmlToFabric,
      },
      globalCreationsParams,
    );
  }
}
